/**
 * "Forgot password" links — minting, and recognising one that came back dead.
 *
 * The owner-side twin of this file is `orgSetupLink.ts`, and the pair exists
 * because the two links are genuinely hard to tell apart: both are GoTrue
 * `type=recovery` tokens routed through /api/auth/callback, so the token type
 * says nothing. What separates them is who is holding it. A gym owner opening a
 * setup link has never had a password. Someone opening a reset link already has
 * one and is replacing it.
 *
 * Getting that backwards is what this module is for. An expired reset link used
 * to be matched by `next?.includes('/setup')` on /signin, which is true of the
 * owner setup path as well, so an established owner who asked to reset a
 * password was handed a panel offering to help them "finish setting up" the
 * account they had been using for months.
 *
 * WHY THE MARKER LIVES IN `next` — do not "improve" this into a separate param.
 * These links are emailed through the Supabase send_email hook, and
 * `api/auth/hook/send-email/route.ts:123` rebuilds the callback URL from
 * scratch, carrying over `next` and nothing else. A `purpose=password_reset`
 * hung off `redirectTo` would look right at the minting end, survive review,
 * and silently never arrive. `next` is the only field that makes the round
 * trip, which is why `orgSetupLink` encodes its discriminator there too.
 */

/**
 * The `next` carried by every password-reset link: the screen that actually
 * asks for a new password.
 *
 * Until this constant changed, resets carried `/setup` — a stub whose only
 * behaviour is `router.replace('/dashboard/admin')`. So "Forgot password" did
 * not reset a password. It signed you in, dropped you on the dashboard, and
 * never asked for a new one; and you could not set one afterwards either,
 * because every reachable password form (profile, /auth/change-password,
 * mobile) requires the CURRENT password — the one thing the person had just
 * told us they had forgotten.
 *
 * /auth/reset-password was written for exactly this and then orphaned. Its own
 * validate step already handles arriving with a session established by
 * /api/auth/callback ("User is already authenticated, skipping token
 * validation") and shows the form; the hash-token branch below it is the
 * legacy implicit-flow path. Pointing resets here connects a screen that was
 * already built, tested, and unreachable.
 */
export const PASSWORD_RESET_NEXT_PATH = '/auth/reset-password';

/**
 * The destination resets carried before the constant above changed.
 *
 * A reset link lives for `mailer_otp_exp`, so for that long after this ships
 * there are real links in real inboxes still pointing at `/setup`. They still
 * work (they land on the dashboard, exactly as they did before), and when one
 * of them dies it must still be recognised as a RESET on /signin — otherwise
 * the fix for that panel's wording regresses for everyone mid-flight. Nothing
 * mints this any more; only the detector still answers to it.
 */
const LEGACY_PASSWORD_RESET_NEXT_PATH = '/setup';

/**
 * Build the `redirectTo` handed to `supabase.auth.resetPasswordForEmail`.
 *
 * All three reset endpoints (`/api/auth/reset-password`,
 * `/api/auth/reset-password-v2`, `/api/public/auth/reset-password`) send the
 * same email and must shape the link identically — they previously repeated the
 * URL by hand in three places, which is how a discriminator drifts.
 */
export function buildPasswordResetRedirectTo(baseUrl: string): string {
  return `${baseUrl}/api/auth/callback?next=${encodeURIComponent(PASSWORD_RESET_NEXT_PATH)}`;
}

/**
 * True when `next` marks this as a password-reset link.
 *
 * Accepts the legacy destination as well as the current one, so a link already
 * sitting in someone's inbox still gets reset wording when it dies. Both are
 * exact comparisons: neither may be loosened to a prefix or substring, because
 * the owner setup path `/onboarding/setup-password` contains `/setup`, and
 * conflating the two is the entire bug this pair exists to prevent.
 *
 * `next` arrives already decoded and open-redirect-checked by
 * `validateInternalPath`, so this compares the decoded form — the same contract
 * as `isOrgSetupNext`.
 */
export function isPasswordResetNext(next: string | null | undefined): boolean {
  return next === PASSWORD_RESET_NEXT_PATH || next === LEGACY_PASSWORD_RESET_NEXT_PATH;
}
